import { useState, useEffect } from "react";
import { Sparkles, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/enhanced-button";
import type { VirtualPLEntryDto } from "@blinkbooks/types";
import {
  listVirtualPLEntries,
  createVirtualPLEntry,
  deleteVirtualPLEntry,
} from "@/services/virtualPlEntry.service";
import { getView } from "@/services/view.service";
import { CreateVirtualEntryForm } from "@/components/CreateVirtualEntryForm";
import { SECTION_KEY_OPTIONS, type CreateVirtualPlEntryFormValues } from "@/schemas/virtualPlEntry.schema";
import { formatCurrency } from "@/components/sidebar-components/Reports/utils";
import { useToast } from "@/hooks/use-toast";
import posthog from "@/lib/posthog";

interface ManageVirtualEntriesModalProps {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  // Set when a multi-company View is active — the modal then lets the user pick
  // which of the view's companies each entry belongs to.
  activeViewId?: string | null;
}

const sectionLabel = (key: string) => SECTION_KEY_OPTIONS.find((o) => o.value === key)?.label ?? key;

const formatMonth = (effectiveMonth: string): string => {
  const [year, month] = effectiveMonth.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

export function ManageVirtualEntriesModal({ open, onClose, onChanged, activeViewId }: ManageVirtualEntriesModalProps) {
  const [entries, setEntries] = useState<VirtualPLEntryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const { toast } = useToast();
  const companyNameById = new Map(companies.map((c) => [c.id, c.name]));

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      listVirtualPLEntries(),
      activeViewId ? getView(activeViewId).then((view) => view.organizations) : Promise.resolve([]),
    ])
      .then(([data, orgs]) => {
        setEntries(data);
        setCompanies(orgs);
        posthog.capture("virtual_entries_modal_opened", { existing_entry_count: data.length, is_view: !!activeViewId });
      })
      .finally(() => setLoading(false));
  }, [open, activeViewId]);

  const handleAdd = async (values: CreateVirtualPlEntryFormValues) => {
    const created = await createVirtualPLEntry({
      sectionKey: values.sectionKey,
      lineName: values.lineName,
      amount: values.amount,
      effectiveMonth: `${values.effectiveMonth}-01`,
      description: values.description || undefined,
      organizationId: values.organizationId || undefined,
    });
    setEntries((prev) => [created, ...prev]);
    setShowForm(false);
    posthog.capture("virtual_pl_entry_created", {
      section_key: values.sectionKey,
      effective_month: values.effectiveMonth,
    });
    onChanged();
  };

  const handleDelete = async (id: string) => {
    const deletedEntry = entries.find((e) => e.id === id);
    setEntries((prev) => prev.filter((e) => e.id !== id));
    try {
      await deleteVirtualPLEntry(id);
    } catch {
      if (deletedEntry) {
        setEntries((prev) => [deletedEntry, ...prev]);
      }
      toast({ variant: "destructive", description: "Failed to delete entry. Please try again." });
      return;
    }
    if (deletedEntry) {
      const ageDays = Math.round((Date.now() - new Date(deletedEntry.createdAt).getTime()) / (24 * 60 * 60 * 1000));
      posthog.capture("virtual_pl_entry_deleted", {
        section_key: deletedEntry.sectionKey,
        age_days: ageDays,
      });
    }
    onChanged();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Virtual P&L entries
          </DialogTitle>
          <DialogDescription>
            Temporary forecast adjustments layered onto the consolidated P&L. They never touch QuickBooks.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : entries.length === 0 && !showForm ? (
            <p className="text-sm text-muted-foreground italic py-2">No virtual entries yet.</p>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {entries.map((entry) => (
                <div key={entry.id} className="flex items-center gap-2 rounded-md border p-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{entry.lineName}</p>
                    <p className="text-xs text-muted-foreground">
                      {activeViewId && companyNameById.has(entry.organizationId) && (
                        <>{companyNameById.get(entry.organizationId)} · </>
                      )}
                      {sectionLabel(entry.sectionKey)} · {formatMonth(entry.effectiveMonth)} · {entry.createdByName}
                    </p>
                  </div>
                  <span className="text-sm font-semibold tabular-nums whitespace-nowrap">
                    {formatCurrency(entry.amount)}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} className="h-7 w-7 p-0 shrink-0">
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {showForm ? (
            <div className="pt-2 border-t">
              <CreateVirtualEntryForm
                onSubmit={handleAdd}
                onCancel={() => setShowForm(false)}
                companies={activeViewId ? companies : undefined}
              />
            </div>
          ) : (
            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowForm(true)} className="gap-1">
                <Plus className="h-3.5 w-3.5" /> Add entry
              </Button>
              <Button onClick={onClose}>Done</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
